// src/functions/path_utils.ts
import path from 'path';
import type * as fsType from 'fs';

let fs: typeof fsType;
if (typeof window === 'undefined') {
  // eslint-disable-next-line @typescript-eslint/no-var-requires
  fs = require('fs') as typeof fsType;
}

export function findProjectRoot(): string {
  const candidates = [
    process.env.PROJECT_ROOT,
    process.cwd(),
    '/srv/thedailydialectics/app',
    '/var/www/TDD/thedailydialectics',
    '/var/www/thedailydialectics',
    '/var/www/presites/thedailydialectics/extras/TDD/thedailydialectics',
    path.resolve(__dirname, '..', '..'),
  ].filter(Boolean) as string[];

  for (const c of candidates) {
    try {
      if (fs.existsSync(c) && fs.statSync(c).isDirectory()) return c;
    } catch { }
  }
  // last resort: process.cwd()
  return process.cwd();
}

export function getPagePaths(dirname: string, filename: string) {

  // Content lives in the repo at src/pages. Override with PAGES_ROOT.
  const main_directory = process.env.PAGES_ROOT || path.join(process.cwd(), 'src', 'pages');

  const directory = path.join(main_directory, dirname, filename);
  return {
    jsonPath: path.join(directory, 'variables.json'),
    contentsPath: path.join(directory, 'content.md'),
  };
}

/**
 * Given pageData.thumbnail (could be absolute or relative or a directory),
 * and the page directory on disk, return an absolute path to the "info.json".
 * Throws if not found.
 */
export function resolveThumbnailInfoJson(thumbnailValue: string, pageDirectory: string): string {
  const projectRoot = findProjectRoot();
  const tried: string[] = [];

  if (!thumbnailValue) throw new Error('thumbnail value empty');

  // 1) If it's already an absolute path to a file/directory
  if (path.isAbsolute(thumbnailValue)) {
    // If it points to a directory, append info.json
    let candidate = thumbnailValue.endsWith('info.json') ? thumbnailValue : path.join(thumbnailValue, 'info.json');
    tried.push(candidate);
    if (fs.existsSync(candidate)) return candidate;

    // If path points into a different root we know (/var/www/thedailydialectics), remap to our projectRoot/public


  }

  // Normalise legacy thumbnail values down to a media-relative path anchored at
  // imgs/ or pdfs/: handles absolute URLs (https://host/imgs/…), old absolute
  // roots (/srv/media/.../imgs/…) and bare relative ones (imgs/…).
  let rel = thumbnailValue
    .replace(/^https?:\/\/[^/]+\//i, '') // strip protocol + host
    .replace(/^\/+/, '');                // strip leading slashes
  const anchored = rel.match(/(?:^|\/)((?:imgs|pdfs)\/.+)$/i);
  if (anchored) rel = anchored[1];
  let normalized = rel.endsWith('info.json') ? rel : path.join(rel, 'info.json');

  // 1b) Frozen media root — imgs/pdfs info.json now live under MEDIA_ROOT.
  const mediaRoot = process.env.MEDIA_ROOT || '/mnt/general_storage/FROZEN_MEDIA/thedailydialectics';
  let candidate = path.join(mediaRoot, normalized);
  tried.push(candidate);
  if (fs.existsSync(candidate)) return candidate;

  // 2) Fallback: served from the app's public/ (legacy layout)
  candidate = path.join(projectRoot, 'public', normalized);
  tried.push(candidate);
  if (fs.existsSync(candidate)) return candidate;

  // 3) Try page-local relative directory (some older pages may reference local thumbnail folder)
  candidate = path.join(pageDirectory, thumbnailValue);
  if (!candidate.endsWith('info.json')) candidate = path.join(candidate, 'info.json');
  tried.push(candidate);
  if (fs.existsSync(candidate)) return candidate;

  // 4) Try joining projectRoot/public using exact thumbnailValue (no modification)
  candidate = path.join(projectRoot, 'public', thumbnailValue);
  tried.push(candidate);
  if (fs.existsSync(candidate)) return candidate;

  // nothing found — throw with diagnostics
  const uniqueTried = Array.from(new Set(tried));
  const message = `resolveThumbnailInfoJson: could not find info.json for thumbnail="${thumbnailValue}". Tried:\n` +
    uniqueTried.map(p => ` - ${p}`).join('\n') +
    `\nprojectRoot: ${projectRoot}\npageDirectory: ${pageDirectory}`;
  throw new Error(message);
}
